import { useCallback, useEffect, useState } from 'react';
import { UserSettingsAPI } from '../UserSettingsAPI';
import { isEqual } from 'lodash';

const parseValue = <T>(value: string | undefined): T | undefined => {
  if (value !== undefined && value !== null && value) {
    try {
      return JSON.parse(value) as T;
    } catch {
      return undefined;
    }
  }
  return undefined;
};
/**
 * Custom hook to manage a user setting state with JSON parsing and fallback.
 * @param key The key of the user setting.
 * @param defaultValue The default value to use if the setting is not found or invalid.
 */
export function useUserSettingState<T = any>(key: string, defaultValue: T) {
  const userSettingsResponse = UserSettingsAPI.useGetAllSettingsQuery();
  const { data: valueUserSettings, isLoading: isLoadingUserSettings } =
    userSettingsResponse;
  const [setSetting, { isLoading: isLoadingUserSetting }] =
    UserSettingsAPI.useSetSettingMutation();
  const [data, setData] = useState<T | undefined>(undefined);

  useEffect(() => {
    if (!isLoadingUserSettings && data === undefined) {
      const newValue = parseValue<T>(valueUserSettings?.[key]) ?? defaultValue;
      if (!isEqual(newValue, data)) {
        setData(newValue);
      }
    }
  }, [isLoadingUserSettings, defaultValue, data, valueUserSettings, key]);

  const update = useCallback(
    async (newValue: T, triggerSave?: boolean) => {
      if (isEqual(newValue, data)) {
        // https://github.com/react-grid-layout/react-grid-layout/issues/1984
        return;
      }
      setData(newValue);
      if (!triggerSave) return;

      return await setSetting({
        settingName: key,
        value: JSON.stringify(newValue),
      }).unwrap();
    },
    [data, key, setSetting]
  );

  return {
    ...userSettingsResponse,
    data,
    setData: update,
    isLoading: isLoadingUserSetting || data === undefined,
  };
}
